import type { ComponentType } from '@angular/cdk/portal';
import { Injectable, TemplateRef, inject } from '@angular/core';
import { Observable, map, take } from 'rxjs';

import type { ZardDialogRef } from '@solvace/zard/shared/components/dialog/dialog-ref';
import { ZardDialogOptions } from '@solvace/zard/shared/components/dialog/dialog.component';
import { ZardDialogService } from '@solvace/zard/shared/components/dialog/dialog.service';
import { mergeClasses } from '@solvace/zard/shared/utils/merge-classes';

import { SvzImagesViewerModalComponent } from '../modals/svz-modal-image-viewer/svz-images-viewer-modal.component';
import type { SvzImagesViewerConfig, SvzImagesViewerItem } from '../modals/svz-modal-image-viewer/svz-images-viewer.types';
import { SvzAlertDialogComponent } from './components/svz-alert-dialog/svz-alert-dialog.component';
import { SvzConfirmDialogComponent, type SvzConfirmDialogOkType } from './components/svz-confirm-dialog/svz-confirm-dialog.component';
import { SvzCopilotConfirmDialogComponent } from './components/svz-copilot-confirm-dialog/svz-copilot-confirm-dialog.component';
import { SvzEmbedCodeDialogComponent, type SvzEmbedCodeDialogData } from './components/svz-embed-code-dialog/svz-embed-code-dialog.component';
import { SvzWarningDialogComponent } from './components/svz-warning-dialog/svz-warning-dialog.component';
import type { SvzDialogConfig, SvzDialogContentType } from './models/svz-dialog-config';
import { SvzDialogSizesWidth } from './models/svz-dialog-sizes';

export type SvzDialogRefLike<T = unknown, R = unknown> = Pick<ZardDialogRef<T, R>, 'close' | 'afterClosed' | 'componentInstance'>;

export type SvzConfirmDialogVariant = 'default' | 'design-system';

export interface SvzConfirmDialogOptions {
  title?: string;
  okText?: string;
  cancelText?: string;
  hideCloseButton?: boolean;
  okType?: SvzConfirmDialogOkType;
  cancelType?: 'default' | 'outline-primary';
  /**
   * Additive. `'design-system'` renders the Solvace UI prototypes' `#dialog` (RCA / Alerts / Action Plan register
   * screens): 420px panel capped at `100vw - 48px`, radius 14, padding 22, `0 30px 70px rgba(0,0,0,.3)` shadow, no ✕,
   * 18px/700 `#1c1c1c` title, 14px/1.5 `#717182` text, 20px to the actions, 38px radius-10 buttons right-aligned with a
   * 10px gap, `outline-primary` cancel and the `warning` (prototype `.btn-destructive`) confirm button. Explicit options
   * still win over the variant (AB#72295).
   */
  variant?: SvzConfirmDialogVariant;
  panelClasses?: string;
  titleClasses?: string;
  textClasses?: string;
  buttonClasses?: string;
  actionsClasses?: string;
  outerClasses?: string;
}

const DESIGN_SYSTEM_CONFIRM: Pick<
  SvzConfirmDialogOptions,
  'okType' | 'cancelType' | 'hideCloseButton' | 'outerClasses' | 'panelClasses' | 'titleClasses' | 'textClasses' | 'buttonClasses' | 'actionsClasses'
> = {
  okType: 'warning',
  cancelType: 'outline-primary',
  hideCloseButton: true,
  outerClasses: 'w-[420px] max-w-[calc(100vw-48px)] rounded-[14px] border-0 p-[22px] shadow-[0_30px_70px_rgba(0,0,0,0.3)]',
  panelClasses: 'gap-5 p-0',
  titleClasses: 'text-[18px] leading-[normal] font-bold tracking-normal text-[#1c1c1c]',
  textClasses: 'text-[14px] leading-[1.5] text-[#717182]',
  buttonClasses: 'h-[38px] rounded-[10px] px-4 text-sm font-semibold',
  actionsClasses: 'flex flex-row justify-end gap-[10px]',
};

@Injectable({
  providedIn: 'root',
})
export class SvzDialogService {
  private readonly zardDialog = inject(ZardDialogService);
  private readonly openRefs: ZardDialogRef<unknown>[] = [];

  open<T>(component: SvzDialogContentType<T>, config?: SvzDialogConfig): ZardDialogRef<T> {
    const options = this.buildOptions(component, config);
    const ref = this.zardDialog.create<T, unknown>(options);
    this.trackRef(ref);
    return ref;
  }

  openImage(imagesUrl: string | SvzImagesViewerItem[], config?: SvzImagesViewerConfig): void {
    const options = new ZardDialogOptions<SvzImagesViewerModalComponent, { imagesUrl: string | SvzImagesViewerItem[]; config?: SvzImagesViewerConfig }>();
    options.zContent = SvzImagesViewerModalComponent;
    options.zData = { imagesUrl, config };
    options.zHideFooter = true;
    options.zClosable = false;
    options.zMaskClosable = true;
    options.zCancelText = null;
    options.zOkText = null;
    options.zCustomClasses = 'bg-transparent p-0 border-0 shadow-none max-w-[min(100vw,48rem)] w-full';
    options.zWidth = '100%';

    const ref = this.zardDialog.create(options);
    this.trackRef(ref);
  }

  /**
   * The prototype "Embed this chart" dialog (`anEmbedModal`): the design-system `#sds-dialog` box around a code block
   * and the Close / Copy code actions. Resolves `true` when the snippet was copied, `false` when closed.
   */
  openEmbedCode(code: string, settings?: Omit<SvzEmbedCodeDialogData, 'code'>): Observable<boolean | undefined> {
    const options = new ZardDialogOptions<SvzEmbedCodeDialogComponent, SvzEmbedCodeDialogData>();
    options.zContent = SvzEmbedCodeDialogComponent;
    options.zData = { code, ...settings };
    options.zHideFooter = true;
    options.zClosable = false;
    options.zMaskClosable = true;
    options.zCancelText = null;
    options.zOkText = null;
    options.zCustomClasses =
      settings?.variant === 'sds-dialog'
        ? 'w-[420px] max-w-[calc(100vw-40px)] rounded-[12px] border-0 p-[22px] shadow-[0_12px_40px_rgba(0,0,0,0.25)] max-[640px]:max-w-[360px]'
        : 'w-[420px] max-w-[calc(100vw-40px)] rounded-[14px] border-0 p-[22px] shadow-[0_30px_70px_rgba(0,0,0,0.3)]';
    options.zWidth = '100%';

    const ref = this.zardDialog.create(options);
    this.trackRef(ref);
    return ref.afterClosed();
  }

  openConfirm(text: string, settings?: SvzConfirmDialogOptions): Observable<boolean | undefined> {
    const { variant, ...explicit } = settings ?? {};
    const config = variant === 'design-system' ? { ...DESIGN_SYSTEM_CONFIRM, ...explicit } : explicit;
    const options = new ZardDialogOptions<
      SvzConfirmDialogComponent,
      {
        text: string;
        title?: string;
        okText?: string;
        cancelText?: string;
        hideCloseButton?: boolean;
        okType?: SvzConfirmDialogOkType;
        cancelType?: 'default' | 'outline-primary';
        panelClasses?: string;
        titleClasses?: string;
        textClasses?: string;
        buttonClasses?: string;
        actionsClasses?: string;
      }
    >();
    const { outerClasses, ...dialogData } = config ?? {};
    options.zContent = SvzConfirmDialogComponent;
    options.zData = { text, ...dialogData };
    options.zHideFooter = true;
    options.zClosable = !(config?.hideCloseButton ?? false);
    options.zMaskClosable = true;
    options.zCancelText = null;
    options.zOkText = null;
    options.zCustomClasses = mergeClasses('w-full max-w-[600px]', outerClasses);
    options.zWidth = '100%';

    const ref = this.zardDialog.create(options);
    this.trackRef(ref);
    return ref.afterClosed();
  }

  openWarning(
    text: string,
    config?: {
      title?: string;
      buttonText?: string;
    }
  ): Observable<void> {
    const options = new ZardDialogOptions<SvzWarningDialogComponent, { text: string; title?: string; buttonText?: string }>();
    options.zContent = SvzWarningDialogComponent;
    options.zData = { text, title: config?.title, buttonText: config?.buttonText };
    options.zHideFooter = true;
    options.zClosable = true;
    options.zMaskClosable = true;
    options.zCancelText = null;
    options.zOkText = null;
    options.zCustomClasses = 'w-full max-w-[600px]';
    options.zWidth = '100%';

    const ref = this.zardDialog.create(options);
    this.trackRef(ref);
    return ref.afterClosed().pipe(
      take(1),
      map(() => undefined as void)
    );
  }

  openAlert(
    text: string,
    config?: {
      title?: string;
      buttonText?: string;
      accentColor?: string;
    }
  ): Observable<void> {
    const options = new ZardDialogOptions<SvzAlertDialogComponent, { text: string; title?: string; buttonText?: string; accentColor?: string }>();
    options.zContent = SvzAlertDialogComponent;
    options.zData = {
      text,
      title: config?.title,
      buttonText: config?.buttonText,
      accentColor: config?.accentColor,
    };
    options.zHideFooter = true;
    options.zClosable = true;
    options.zMaskClosable = true;
    options.zCancelText = null;
    options.zOkText = null;
    options.zCustomClasses = 'w-full max-w-[600px]';
    options.zWidth = '100%';

    const ref = this.zardDialog.create(options);
    this.trackRef(ref);
    return ref.afterClosed().pipe(
      take(1),
      map(() => undefined as void)
    );
  }

  openCopilotConfirm(
    text: string,
    config?: {
      title?: string;
      okText?: string;
      cancelText?: string;
      hideCloseButton?: boolean;
    }
  ): Observable<boolean | undefined> {
    const options = new ZardDialogOptions<
      SvzCopilotConfirmDialogComponent,
      {
        text: string;
        title?: string;
        okText?: string;
        cancelText?: string;
        hideCloseButton?: boolean;
      }
    >();
    options.zContent = SvzCopilotConfirmDialogComponent;
    options.zData = { text, ...config };
    options.zHideFooter = true;
    options.zClosable = false;
    options.zMaskClosable = false;
    options.zCancelText = null;
    options.zOkText = null;
    options.zCustomClasses = 'w-full max-w-[600px]';
    options.zWidth = '100%';

    const ref = this.zardDialog.create(options);
    this.trackRef(ref);
    return ref.afterClosed();
  }

  closeAll(): void {
    for (const ref of [...this.openRefs]) {
      ref.close();
    }
    this.openRefs.length = 0;
  }

  private trackRef(ref: ZardDialogRef<unknown>): void {
    this.openRefs.push(ref);
    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        const index = this.openRefs.indexOf(ref);
        if (index >= 0) {
          this.openRefs.splice(index, 1);
        }
      });
  }

  private buildOptions<T>(component: SvzDialogContentType<T>, config?: SvzDialogConfig): ZardDialogOptions<T, unknown> {
    const options = new ZardDialogOptions<T, unknown>();
    options.zContent = component as ComponentType<T> | TemplateRef<T>;
    options.zData = config?.data;
    options.zHideFooter = true;
    options.zClosable = !config?.disableClose && !config?.hideCloseButton;
    options.zMaskClosable = !config?.disableClose;
    options.zCancelText = null;
    options.zOkText = null;

    let minWidth = config?.minWidth;
    let maxWidth = config?.maxWidth;
    if (config?.size) {
      const sizeWidth = SvzDialogSizesWidth[config.size];
      minWidth = minWidth ?? sizeWidth;
      maxWidth = maxWidth ?? sizeWidth;
    }

    const panelClass = config?.panelClass ? (Array.isArray(config.panelClass) ? config.panelClass.join(' ') : config.panelClass) : '';

    const widthParts = ['w-full'];
    if (maxWidth) {
      widthParts.push(`max-w-[${maxWidth}]`);
    }
    if (minWidth) {
      widthParts.push(`min-w-[${minWidth}]`);
    }
    if (panelClass) {
      widthParts.push(panelClass);
    }
    options.zCustomClasses = widthParts.join(' ');
    options.zWidth = config?.width ?? '100%';

    return options;
  }
}
